import {
  Navigate,
  Route,
  Routes,
  BrowserRouter,
  useLocation,
} from "react-router-dom";
import SentimentDashboard from "./components/Admin/SentimentDashboard";
import SectionDashboard from "./components/Admin/SectionDashboard";
import ReportGen from "./components/GeneratePdf/ReportGen";
import DecoyHomePage from "./components/CommonPage/Home";
import PreviewReport from "./components/GeneratePdf/PreviewReport";
import PasswordRequest from "./components/PasswordRequest";
import CrawledInformation from "./components/UserLevel/Crawler Dashboard/CrawledInformation";
import CrawledNews from "./components/UserLevel/Crawler Dashboard/News Crawler/CrawledNews";
import CrawlerLiveSearch from "./components/UserLevel/Crawler Dashboard/Social Media Crawler/CrawlerMain/CrawlerLiveSearch";
import DataCardsChild from "./components/UserLevel/DataCards/DataCardsChild";
import DownloadHistory from "./components/GeneratePdf/DownloadHistory";
import AdminPanel from "./components/Admin/Dashboard/AdminPanel";
import ProtectedRoute, { LoadingScreen } from "./routes/ProtectedRoute";
import FlowDiagram from "./components/CommonPage/FlowDiagram";
import ViolationRule from "./components/CommonPage/ViolationRule";
import FactChecker from "./components/UserLevel/Fake News/FactChecker/FactChecker";
import AddUserVerify from "./components/Authentication/UserVerification/AddUserVerify";
import ForgetChangePassword from "./components/ForgetChangePassword";
import ErrorPage from "./ErrorPage";
import SessionLogsVerify from "./components/Authentication/UserVerification/SessionLogsVerify";
import NewsApp from "./components/UserLevel/Crawler Dashboard/News Crawler/NewsScope";
import FakeNews from "./components/UserLevel/Fake News/FakeNewsModule/FakeNewsMain";
import TopicQuery from "./components/UserLevel/Crawler Dashboard/Automated Crawler/TopicQuery";
import ImageAnalysisMain from "./components/UserLevel/Image Analysis/ImageAnalysisMain";
import AudioAnalysisMain from "./components/UserLevel/Audio Analysis/AudioAnalysisMain";
import { useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import HomePage from "./pages/HomePage";
import Dashboard from "./pages/Dashboard";
import ChatInterface from "./components/CommonPage/ChatInterface";
import { ToastContainer } from "react-toastify";


// ✅ Import Chat

// ─────────────────────────────────────────────────────────────
// Root redirect — waits for session check, then routes correctly
// ─────────────────────────────────────────────────────────────
function RootRedirect() {
  const { loading, isAuthenticated } = useAuth();
  if (loading) return <LoadingScreen />;
  return <Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />;
}

// ─────────────────────────────────────────────────────────────
// Wrapper (so we can use useLocation safely)
// ─────────────────────────────────────────────────────────────
function AppWrapper() {
  const { user } = useAuth();
  const userDetails = user || {};
  const location = useLocation();

  // ❌ Routes where chat should NOT appear
  const hiddenRoutes = [
    "/login",
    "/password/forget",
    "/password/reset",
    "/socialMediaCrawler",
    "/previewreport",
    "/reportgen",
    "/audioAnalysis",
  ];

  // Supports dynamic routes too
  // "/" (home page) is matched exactly — every path startsWith "/"
  const shouldShowChat =
    location.pathname !== "/" &&
    !hiddenRoutes.some((route) => location.pathname.startsWith(route));

  return (
    <>
      <Routes>
        {/* <Route path="/" element={<RootRedirect />} /> */}
        <Route path="/" element={<HomePage />} />

        <Route path="/login" element={<Login />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboardSentiment"
          element={
            <ProtectedRoute>
              <SentimentDashboard userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboardSection"
          element={
            <ProtectedRoute>
              <SectionDashboard userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/factChecker"
          element={
            <ProtectedRoute>
              <FactChecker userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/fakeNews"
          element={
            <ProtectedRoute>
              <FakeNews userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/analyzedData"
          element={
            <ProtectedRoute>
              <DataCardsChild userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/previewreport"
          element={
            <ProtectedRoute>
              <PreviewReport userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/adminpanel"
          element={
            <ProtectedRoute>
              <AdminPanel userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route path="/home" element={<DecoyHomePage />} />

        <Route
          path="/reportgen"
          element={
            <ProtectedRoute>
              <ReportGen />
            </ProtectedRoute>
          }
        />

        <Route
          path="/downloadHistory"
          element={
            <ProtectedRoute>
              <DownloadHistory userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/adduser"
          element={
            <ProtectedRoute>
              <AddUserVerify userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/crawledSessionRecords/:platform"
          element={
            <ProtectedRoute>
              <SessionLogsVerify userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route path="/password/forget" element={<PasswordRequest />} />
        <Route path="/password/reset" element={<ForgetChangePassword />} />

        <Route
          path="/socialMediaCrawler"
          element={
            <ProtectedRoute>
              <CrawlerLiveSearch userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/automatedCrawler"
          element={
            <ProtectedRoute>
              <TopicQuery userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/newsSentiment"
          element={
            <ProtectedRoute>
              <CrawledNews userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/audioAnalysis"
          element={
            <ProtectedRoute>
              <AudioAnalysisMain userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/crawledImage"
          element={
            <ProtectedRoute>
              <ImageAnalysisMain userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/crawledInformation"
          element={
            <ProtectedRoute>
              <CrawledInformation userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/flowdiagram"
          element={
            <ProtectedRoute>
              <FlowDiagram userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/violationrules"
          element={
            <ProtectedRoute>
              <ViolationRule userDetails={userDetails} />
            </ProtectedRoute>
          }
        />

        <Route
          path="/NewsLens"
          element={
            <ProtectedRoute>
              <NewsApp userDetails={userDetails} />
            </ProtectedRoute>
          }
        />
        {/* <Route path="/feedbackRepository" element={ <ProtectedRoute> <FeedbackRepo userDetails={userDetails} /> </ProtectedRoute> } /> */}

        {/* <Route path="/sentimentCrawler" element={ <ProtectedRoute> <SentimentCrawler userDetails={userDetails} /> </ProtectedRoute> } /> */}
        <Route path="*" element={<ErrorPage />} />
      </Routes>

      {/* ✅ Chat rendered conditionally */}
      {shouldShowChat && <ChatInterface />}
    </>
  );
}



  export const TOAST_STYLE = {
    fontFamily: "'Host Grotesk', sans-serif",
    fontSize: "13px",
    fontWeight: 500,
    borderRadius: "12px",
    padding: "14px 18px",
    boxShadow:
      "0 8px 32px -4px rgba(0,0,0,0.12), 0 2px 8px -2px rgba(0,0,0,0.06)",
    border: "1px solid #E2E8F0",
    color: "#1E293B",
    lineHeight: 1.4,
    letterSpacing: "-0.1px",
    backdropFilter: "blur(8px)",
  };

  export const TOAST_PROGRESS_STYLE = {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    height: "3px",
    borderRadius: "0 0 12px 12px",
  };


// ─────────────────────────────────────────────────────────────
// Main App
// ─────────────────────────────────────────────────────────────
function App() {
  return (
    <BrowserRouter>

       <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        pauseOnHover
        draggable={false}
        toastStyle={TOAST_STYLE}
        progressStyle={TOAST_PROGRESS_STYLE}
      />



      <AppWrapper />
    </BrowserRouter>
  );
}

export default App;
